import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Minimize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import HeaderBar from "../../components/showcase/HeaderBar";
import { WheelChart } from "../../components/showcase/WheelChart";
import ProductDetailPanel from "../../components/showcase/ProductDetailPanel";
import BlockersPanel from "../../components/showcase/BlockersPanel";
import WinsList from "../../components/showcase/WinsList";
import PresentationPinBar from "../../components/showcase/PresentationPinBar";
import { NotesProvider } from "../../components/showcase/NotesContext";
import { PresentationContext, usePresentationView } from "../../components/showcase/PresentationContext";
import { RoadmapVisibilityContext, useRoadmapVisibility, markRoadmapItem, presentationRoadmap } from "../../components/showcase/RoadmapVisibilityContext";
import showcaseApi from "../../lib/showcaseApi";

function DashboardInner() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const detailRef = useRef(null);
  const { containerRef, presenting, fullscreenUnavailable, enter, exit } = usePresentationView();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setError("");
    try {
      const r = await showcaseApi.overview();
      setData(r);
    } catch (err) {
      if (err.status === 401) {
        navigate("/showcase/login", { replace: true });
        return;
      }
      setError(err.message || "We couldn't fetch the latest update. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);
  const handleLogout = async () => {
    try { await showcaseApi.logout(); } catch (_) {}
    navigate("/showcase/login", { replace: true });
  };
  const onVisibilityChanged = useCallback((key, excluded, id) => {
    setData((current) => markRoadmapItem(current, key, excluded, id));
  }, []);
  const visibility = useRoadmapVisibility(onVisibilityChanged);
  const refreshDashboard = () => Promise.all([load(), visibility.refresh()]);
  const viewData = useMemo(() => presenting ? presentationRoadmap(data) : data, [data, presenting]);
  const products = useMemo(() => viewData?.products || [], [viewData]);
  const updateDescription = (updated) => setData((current) => current ? {
    ...current,
    products: current.products.map((product) => product.slug === updated.slug ? { ...product, tagline: updated.tagline } : product),
  } : current);
  const selection = products.find((p) => p.slug === selected);
  const openDetails = () => {
    const detail = detailRef.current;
    if (!detail) return;
    detail.focus({ preventScroll: true });
    detail.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth", block: "start" });
  };

  return (
    <PresentationContext.Provider value={presenting}>
      <RoadmapVisibilityContext.Provider value={visibility}>
      <div ref={containerRef} className={`showcase-view ${presenting ? "showcase-view--presenting" : ""}`} data-testid="showcase-fullscreen-container" data-presenting={presenting}>
        <div className="min-h-screen w-full" data-testid="showcase-dashboard">
          {presenting ? (
            <header className="presentation-toolbar" data-testid="showcase-presentation-toolbar">
              <div className="min-w-0">
                <p className="eyebrow" data-testid="presentation-brand">LiftTech Portfolio</p>
                <p className="font-heading text-xl font-semibold" data-testid="presentation-title">What’s happening across our products</p>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <span className="hidden sm:inline text-xs text-muted-foreground" data-testid="presentation-exit-hint">Esc to exit</span>
                <Button onClick={exit} className="min-h-11 gap-2" data-testid="showcase-exit-presentation-mode-button"><Minimize2 size={16} aria-hidden="true" />Exit</Button>
              </div>
            </header>
          ) : (
            <HeaderBar lastUpdated={data?.last_updated} onLogout={handleLogout} onRefresh={refreshDashboard} onPresent={enter} presentationDisabled={loading || !products.length}
              subtitle="What our team has done, what’s next, and where a decision is needed." />
          )}

          <div className="showcase-content mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6 sm:space-y-8">
            {presenting && fullscreenUnavailable && <p role="status" className="text-xs text-muted-foreground" data-testid="showcase-fullscreen-fallback-note">Fullscreen is not available in this browser. You can still present in this view.</p>}
            {error && (
              <Alert variant="destructive" data-testid="dashboard-error-alert">
                <AlertTitle>Something went wrong</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
                {!presenting && <Button variant="outline" className="mt-3" onClick={load} data-testid="dashboard-error-retry">Try again</Button>}
              </Alert>
            )}
            {!presenting && (loading ? <Skeleton className="h-16 w-full rounded-xl" /> : <PresentationPinBar pin={data?.pin} onPinned={refreshDashboard} onDescriptionChanged={updateDescription} />)}

            <section data-testid="dashboard-wheel-section">
              <div className={presenting ? "sr-only" : "flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between mb-4"}>
                <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-foreground" data-testid="dashboard-wheel-title">Each product at a glance</h2>
                <p className="text-xs sm:text-sm text-muted-foreground" data-testid="dashboard-wheel-hint">Select a product for a quick summary and more detail below.</p>
              </div>
              {loading ? <Skeleton className="h-[420px] w-full rounded-xl" /> : <WheelChart products={products} selected={selected} onSelect={setSelected} onOpenDetails={openDetails} />}
            </section>

            {!loading && selection && (
              <section ref={detailRef} tabIndex={-1} aria-label="Selected product detail" className="showcase-detail-section focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring" data-testid="dashboard-detail-section">
                <ProductDetailPanel selection={selection} isHub={false} />
              </section>
            )}
            {(!presenting || viewData?.blockers?.length > 0) && <section data-testid="dashboard-blockers-section">
              {loading ? <Skeleton className="h-64 w-full rounded-xl" /> : <BlockersPanel items={viewData?.blockers || []} />}
            </section>}
            {(!presenting || viewData?.wins?.length > 0) && <section data-testid="dashboard-recent-wins">
              {loading ? <Skeleton className="h-48 w-full rounded-xl" /> : <WinsList items={viewData?.wins || []} />}
            </section>}
            {!presenting && <div className="eyebrow pt-4 pb-8 text-center text-muted-foreground" data-testid="dashboard-footer">Portfolio updates · refreshed each visit</div>}
          </div>
        </div>
      </div>
      </RoadmapVisibilityContext.Provider>
    </PresentationContext.Provider>
  );
}

export default function ShowcaseDashboardPage() {
  return <NotesProvider><DashboardInner /></NotesProvider>;
}
